import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { t } from "../i18n";

/** Bouquet photos: arrows, swipe, keyboard and a thumbnail strip. */
export const ProductGallery = ({ images = [], alt = "" }) => {
  const { locale = "en" } = useParams();
  const [i, setI] = useState(0);
  const touch = useRef(null);
  const count = images.length;

  useEffect(() => { setI(0); }, [images]);

  const go = (step) => setI((v) => (v + step + count) % count);

  useEffect(() => {
    if (count < 2) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  if (!count) return <div className="w-full aspect-[4/5] bg-plaster-deep" data-testid="product-gallery-empty" />;

  return (
    <div data-testid="product-gallery">
      <div
        className="relative overflow-hidden bg-plaster-deep group select-none"
        onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touch.current === null) return;
          const dx = e.changedTouches[0].clientX - touch.current;
          touch.current = null;
          if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
        }}
      >
        {images.map((img, idx) => (
          <img
            key={img}
            src={img}
            alt={idx === i ? alt : ""}
            loading={idx === 0 ? "eager" : "lazy"}
            data-testid={idx === i ? "product-main-image" : undefined}
            className={`w-full aspect-[4/5] object-cover transition-opacity duration-300 ${idx === i ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none"}`}
          />
        ))}

        {count > 1 ? (
          <>
            <button type="button" aria-label={t(locale, "prevPhoto")} data-testid="product-gallery-prev"
                    onClick={() => go(-1)}
                    className="absolute start-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-plaster/85 text-ink border border-brass-light opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 hover:bg-plaster">
              <ChevronLeft size={18} />
            </button>
            <button type="button" aria-label={t(locale, "nextPhoto")} data-testid="product-gallery-next"
                    onClick={() => go(1)}
                    className="absolute end-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-plaster/85 text-ink border border-brass-light opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 hover:bg-plaster">
              <ChevronRight size={18} />
            </button>
            <span className="absolute bottom-2 end-2 text-[11px] tabular bg-ink/70 text-plaster px-2 py-0.5"
                  data-testid="product-gallery-counter">{i + 1} / {count}</span>
          </>
        ) : null}
      </div>

      {count > 1 ? (
        <div className="flex gap-3 mt-3 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
          {images.map((img, idx) => (
            <button key={img} type="button" onClick={() => setI(idx)} data-testid={`product-thumb-${idx}`}
                    aria-label={`Photo ${idx + 1}`} aria-current={idx === i}
                    className={`w-16 sm:w-20 shrink-0 aspect-[4/5] overflow-hidden border transition-colors duration-200 snap-start ${idx === i ? "border-brass" : "border-transparent hover:border-brass-light"}`}>
              <img src={img} alt="" loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default ProductGallery;
